import React, { useEffect, useState } from "react";
import { Card, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause } from "@fortawesome/free-solid-svg-icons";
import styles from "./Dua.module.scss";

import dua1Image from "assets/Duas/dua1.png";
import dua2Image from "assets/Duas/dua2.png";
import dua3Image from "assets/Duas/dua3.png";
import dua4Image from "assets/Duas/dua4.png";

import useFetch from "hooks/useFetch";
import DuaPageSkeleton from "./DuaSkeleton/DuaSkeleton";
import { Button } from "stories";

interface Dua {
  id: number;
  audioId: number;
  title: string;
  transliteration: string;
  translation: string;
  reference: string;
  sound: string | null;
  imageUrl: string;
}

const imageMapping: Record<number, string> = {
  1: dua1Image,
  2: dua2Image,
  3: dua3Image,
  4: dua4Image,
};

const soundMapping: Record<number, string | null> = {
  1: "https://deenbucket.ams3.cdn.digitaloceanspaces.com/IFASTE/AUDIO/faste1.wav",
  2: "https://deenbucket.ams3.cdn.digitaloceanspaces.com/IFASTE/AUDIO/faste2.wav",
  3: "https://deenbucket.ams3.cdn.digitaloceanspaces.com/IFASTE/AUDIO/faste3.wav",
  4: "https://deenbucket.ams3.cdn.digitaloceanspaces.com/IFASTE/AUDIO/faste4.wav",
};

const DuaPage: React.FC = () => {
  const [duas, setDuas] = useState<Dua[]>();
  const [selectedDua, setSelectedDua] = useState<Dua | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const { execute } = useFetch();

  const fetchDuas = async () => {
    const duaResult = (await execute("/api/v1/duas", "GET")) as Dua[];
    if (duaResult) {
      const updatedDuas = duaResult.map((dua) => ({
        ...dua,
        imageUrl: imageMapping[dua.id] || "",
        sound: soundMapping[dua.id] || null,
      }));
      setDuas(updatedDuas);
    }
  };

  useEffect(() => {
    fetchDuas();
  }, []);

  const handlePlayPause = (dua: Dua) => {
    const audio = document.getElementById(
      `audio-${dua.id}`,
    ) as HTMLAudioElement;
    if (audio) {
      if (playingId === dua.id) {
        audio.pause();
        setPlayingId(null);
      } else {
        if (playingId !== null) {
          const playingAudio = document.getElementById(
            `audio-${playingId}`,
          ) as HTMLAudioElement;
          playingAudio?.pause();
        }
        audio.play();
        setPlayingId(dua.id);
      }
    }
  };

  const handleEditClick = (dua: Dua) => {
    setSelectedDua(dua);
  };

  const handleSaveChanges = async () => {
    if (selectedDua) {
      const payload = {
        title: selectedDua.title,
        transliteration: selectedDua.transliteration,
        translation: selectedDua.translation,
        reference: selectedDua.reference,
      };

      const response = await execute(
        `/api/v1/duas/${selectedDua.id}`,
        "PATCH",
        payload,
      );

      if (response) {
        setDuas((prev) =>
          prev?.map((dua) =>
            dua.id === selectedDua.id ? { ...dua, ...payload } : dua,
          ),
        );
        setSelectedDua(null);
        alert("Dua oppdatert!");
      }
    }

    fetchDuas();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setSelectedDua((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  if (!duas) {
    return <DuaPageSkeleton />;
  }

  return (
    <div className="container mt-4">
      <h1>Oppdater Ramadan Duas</h1>
      <hr />

      <div className="dua-list">
        {duas &&
          duas.map((dua) => (
            <Card key={dua.id} className="mb-3">
              <Card.Img
                variant="top"
                src={dua.imageUrl}
                alt={dua.title}
                className={styles.duaCardImage}
              />
              <Card.Header className={styles.cardHeader}>
                <strong>{dua.title}</strong>{" "}
                {dua.reference && <span>({dua.reference})</span>}
              </Card.Header>
              <Card.Body className={styles.cardBody}>
                <div className={styles.contentContainer}>
                  <div className={styles.textContent}>
                    <p>
                      <strong>Translitterasjon:</strong> {dua.transliteration}
                    </p>
                    <p>
                      <strong>Oversettelse:</strong> {dua.translation}
                    </p>
                    <Button variant="info" onClick={() => handleEditClick(dua)}>
                      Rediger
                    </Button>
                  </div>
                  {dua.sound && (
                    <div className={styles.audioPlayer}>
                      <audio id={`audio-${dua.id}`} src={dua.sound}></audio>
                      <button
                        className={styles.playButton}
                        onClick={() => handlePlayPause(dua)}
                      >
                        <FontAwesomeIcon
                          icon={playingId === dua.id ? faPause : faPlay}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
      </div>

      <Modal show={!!selectedDua} onHide={() => setSelectedDua(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rediger Dua</Modal.Title>
        </Modal.Header>
        {selectedDua && (
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Tittel</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={selectedDua.title}
                  onChange={handleInputChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Oversettelse</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="translation"
                  value={selectedDua.translation}
                  onChange={handleInputChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Translitterasjon</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="transliteration"
                  value={selectedDua.transliteration}
                  onChange={handleInputChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Referanse</Form.Label>
                <Form.Control
                  type="text"
                  name="reference"
                  value={selectedDua.reference}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedDua(null)}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Lagre Endringer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DuaPage;
